import { NgZone } from '@angular/core';
import { Auth, onAuthStateChanged, User } from 'firebase/auth';
import {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  onSnapshot,
  Query,
  QueryDocumentSnapshot,
  SnapshotOptions,
} from 'firebase/firestore';
import { Observable } from 'rxjs';

export interface DataOptions {
  idField?: string;
}

/**
 * Firestore/Auth callbacks often run outside Angular's zone. Without re-entering the zone,
 * plain field updates (e.g. `isLoading = false`) may not paint until the next CD cycle —
 * commonly seen as a stuck spinner on hard refresh when there is no local cache hit.
 *
 * Prefer signal UI state for new/edited screens; this zone bridge remains the safety net
 * for existing plain-field subscribers.
 */
let angularZone: NgZone | undefined;

export function attachAngularZone(zone: NgZone): void {
  angularZone = zone;
}

function runInZone(fn: () => void): void {
  if (angularZone) {
    angularZone.run(fn);
  } else {
    fn();
  }
}

function snapToData<T extends DocumentData>(
  snapshot: DocumentSnapshot<T>,
  options?: DataOptions & SnapshotOptions,
): T | undefined {
  if (!snapshot.exists()) {
    return undefined;
  }
  const data = snapshot.data(options) as T;
  if (options?.idField) {
    return { ...data, [options.idField]: snapshot.id };
  }
  return data;
}

/** Emits the signed-in user (or null), matching @angular/fire authState. */
export function authState(auth: Auth): Observable<User | null> {
  return new Observable((subscriber) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => runInZone(() => subscriber.next(user)),
      (error) => runInZone(() => subscriber.error(error)),
    );
    return { unsubscribe };
  });
}

/** Real-time document data Observable, matching @angular/fire docData. */
export function docData<T = DocumentData>(
  ref: DocumentReference<T>,
  options?: DataOptions,
): Observable<T | undefined> {
  return new Observable((subscriber) => {
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) =>
        runInZone(() => subscriber.next(snapToData(snapshot, options) as T | undefined)),
      (error) => runInZone(() => subscriber.error(error)),
    );
    return { unsubscribe };
  });
}

/** Real-time collection/query data Observable, matching @angular/fire collectionData. */
export function collectionData<T = DocumentData>(
  query: Query<T>,
  options?: DataOptions,
): Observable<T[]> {
  return new Observable((subscriber) => {
    const unsubscribe = onSnapshot(
      query,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as T;
          if (options?.idField) {
            return { ...(data as object), [options.idField]: docSnap.id } as T;
          }
          return data;
        });
        runInZone(() => subscriber.next(rows));
      },
      (error) => runInZone(() => subscriber.error(error)),
    );
    return { unsubscribe };
  });
}

/** Real-time QueryDocumentSnapshot[] Observable, matching @angular/fire collectionSnapshots. */
export function collectionSnapshots<T = DocumentData>(
  query: Query<T>,
): Observable<QueryDocumentSnapshot<T>[]> {
  return new Observable((subscriber) => {
    const unsubscribe = onSnapshot(
      query,
      (snapshot) => runInZone(() => subscriber.next(snapshot.docs)),
      (error) => runInZone(() => subscriber.error(error)),
    );
    return { unsubscribe };
  });
}

/** Real-time DocumentSnapshot Observable, matching @angular/fire docSnapshots. */
export function docSnapshots<T = DocumentData>(
  ref: DocumentReference<T>,
): Observable<DocumentSnapshot<T>> {
  return new Observable((subscriber) => {
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => runInZone(() => subscriber.next(snapshot)),
      (error) => runInZone(() => subscriber.error(error)),
    );
    return { unsubscribe };
  });
}
